/** Typed wrappers over the run (execution) read + cancel + result endpoints. */
import type {
  CancelRunBody,
  CancelRunResponse,
  Paginated,
  RunDetail,
  RunResultView,
  RunStatus,
  RunSummary
} from '@portal/contracts'
import { apiFetch, PortalApiError } from './portal-client'

export interface RunListParams {
  status?: RunStatus
  flowId?: string
  limit?: number
  cursor?: string
}

function buildQuery(params: RunListParams): string {
  const q = new URLSearchParams()
  if (params.status) q.set('status', params.status)
  if (params.flowId) q.set('flowId', params.flowId)
  if (params.limit != null) q.set('limit', String(params.limit))
  if (params.cursor) q.set('cursor', params.cursor)
  const s = q.toString()
  return s ? `?${s}` : ''
}

/** Page through runs. `status` filters to a single lifecycle state. */
export function listRuns(params: RunListParams = {}): Promise<Paginated<RunSummary>> {
  return apiFetch<Paginated<RunSummary>>(`/api/runs${buildQuery(params)}`)
}

/** Runs whose last reply failed and that can still be answered — the same set the Home's "needs you" list draws on. */
export function listAnswerableRuns(): Promise<Paginated<RunSummary>> {
  return apiFetch<Paginated<RunSummary>>('/api/runs/answerable')
}

/** Full run detail — includes persisted `events` and `pendingSuspension` when awaiting. */
export function getRun(id: string): Promise<RunDetail> {
  return apiFetch<RunDetail>(`/api/runs/${encodeURIComponent(id)}`)
}

/** Server-side cancel: tombstones an awaiting suspension OR aborts a running stream. */
export function cancelRun(id: string, body: CancelRunBody = {}): Promise<CancelRunResponse> {
  return apiFetch<CancelRunResponse>(`/api/runs/${encodeURIComponent(id)}/cancel`, {
    method: 'POST',
    body: JSON.stringify(body)
  })
}

/**
 * The persisted outcome (reply + deliverable file list) captured by the BFF
 * as the run streamed — available even if the launching tab was closed.
 * Rejects with a 404 PortalApiError when no result is stored: code
 * `result_pending` while the BFF is still preparing it (its files are being
 * pulled into the store), `result_not_found` when there is none to wait for.
 */
export function getRunResult(id: string): Promise<RunResultView> {
  return apiFetch<RunResultView>(`/api/runs/${encodeURIComponent(id)}/result`)
}

/** How often the run page asks again while the BFF says the result is still being prepared. */
export const RESULT_PENDING_RETRY_MS = 3_000

/** The BFF is still preparing this run's result: ask again. Never a guess from this browser's clock. */
export function resultPending(err: unknown): boolean {
  return err instanceof PortalApiError && err.status === 404 && err.issue?.code === 'result_pending'
}

/**
 * `retry` for the result query: for as long as the BFF says `result_pending`; never on any other 404 (no result to
 * wait for — a mail-reader run, a lost stream, a failed capture: the page shows the run's status and issues at once);
 * twice for anything else.
 */
export function retryResult(failureCount: number, err: unknown): boolean {
  if (resultPending(err)) return true
  if (err instanceof PortalApiError && err.status === 404) return false
  return failureCount < 2
}

/** `retryDelay` for the result query: every 3 s while pending, backing off otherwise. */
export function retryResultDelay(failureCount: number, err: unknown): number {
  return resultPending(err) ? RESULT_PENDING_RETRY_MS : Math.min(1_000 * 2 ** failureCount, 8_000)
}

/** Stable download URL for one deliverable file (same-origin, cookie-authed). */
export function runFileUrl(id: string, index: number): string {
  return `/api/runs/${encodeURIComponent(id)}/files/${index}`
}

/** Download URL for one LAUNCH input attachment ("what was asked"). */
export function runInputFileUrl(id: string, index: number): string {
  return `/api/runs/${encodeURIComponent(id)}/input/files/${index}`
}

/** Download URL for one HITL reply's attachment (round `replyIndex`). */
export function runReplyFileUrl(id: string, replyIndex: number, index: number): string {
  return `/api/runs/${encodeURIComponent(id)}/input/replies/${replyIndex}/files/${index}`
}
